import { sqliteTable, text, integer, uniqueIndex, index, check } from "drizzle-orm/sqlite-core";
import { sql } from "drizzle-orm";

// ============================================================================
// doctype_registry — Central registry of every doctype a module registers via
// `defineDoctype()`. Foundation for the Doctype Admin Epic (#347).
//
// Scoping model (ERPNext-style):
//   - company_id = NULL  → platform default row (shipped by module code)
//   - company_id = <ULID> → per-tenant override (user-customized)
//
// Natural uniqueness is (id, company_id): one default row per id + one
// override per (id, company). Two defense layers enforce this:
//   1. UNIQUE (id, company_id) — rejects dup per-company overrides.
//   2. Partial UNIQUE on id WHERE company_id IS NULL — rejects dup defaults
//      at the DB level (SQLite UNIQUE alone treats NULLs as distinct).
// ============================================================================

/**
 * Capability flags a doctype opts into. Derived from DOCTYPE_FEATURES so the
 * const and the union can never drift.
 */
export const DOCTYPE_FEATURES = [
  "naming",
  "custom_fields",
  "workflow",
  "print",
  "permissions",
  "automation",
  "list_view",
] as const;

export type DoctypeFeature = (typeof DOCTYPE_FEATURES)[number];

/**
 * Table semantics for the Doctype Catalog (#644). This is metadata-only:
 * Drizzle schemas and module services remain the source of truth for storage
 * and business behavior.
 */
export const DOCTYPE_KINDS = [
  "normal",
  "child_table",
  "submittable",
  "ledger",
  "junction",
  "system",
  "single",
] as const;

export type DoctypeKind = (typeof DOCTYPE_KINDS)[number];

/**
 * Runtime guard — use when reading the JSON `features` column back from D1
 * to reject any unknown capability strings that snuck in. The migration also
 * enforces `json_valid(features)` at the DB level, but per-item validation
 * must happen here.
 */
export function isDoctypeFeature(value: unknown): value is DoctypeFeature {
  return (
    typeof value === "string" &&
    (DOCTYPE_FEATURES as readonly string[]).includes(value)
  );
}

export function isDoctypeKind(value: unknown): value is DoctypeKind {
  return (
    typeof value === "string" &&
    (DOCTYPE_KINDS as readonly string[]).includes(value)
  );
}

export const doctypeRegistry = sqliteTable(
  "core_doctype_registry",
  {
    // Logical doctype id — e.g. "quotation", "invoice".
    // Not globally unique by itself; pair with company_id for the PK pair.
    id: text("id").notNull(),

    // Owning module — e.g. "sales", "accounting".
    moduleId: text("module_id").notNull(),

    // NULL = platform default, non-null = per-company override.
    // No FK to core_companies to keep platform defaults insertable before
    // any tenant exists; integrity is enforced at the service layer.
    companyId: text("company_id"),

    // Physical tenant-DB table name — e.g. "sales_quotations".
    tableName: text("table_name").notNull(),

    // Display label (Thai UI copy).
    label: text("label").notNull(),

    // Optional icon — emoji or lucide icon name.
    icon: text("icon"),

    // JSON array of DoctypeFeature strings.
    // Stored as text; callers must JSON.parse + validate against isDoctypeFeature.
    features: text("features").notNull().default("[]"),

    // Tier — 0 (platform), 1 (Tier 1 module), 2 (Tier 2 module).
    tier: integer("tier").notNull(),

    // #644 — ERPNext-style table classification. Defaults to `normal` so the
    // migration is additive for existing registry rows.
    kind: text("kind").notNull().default("normal"),

    // #644 — optional JSON for child-table relation metadata and generic
    // surface policy. A single JSON column keeps Phase 0 additive while the
    // catalog model is still settling.
    catalogMetadata: text("catalog_metadata"),

    // #365 — where the auto-generated identifier lives (module-declared).
    //   null      → doctype uses ULID (row.id) as the identifier. No
    //               Naming Series is applied; Naming tab renders an info
    //               panel instead of the pattern editor.
    //   "colname" → Naming Series writes its output into this column on
    //               insert. Must match an actual text column on the
    //               doctype's table (validation at defineDoctype() time).
    // The "naming rule" is DERIVED from this field's presence — we don't
    // store a separate rule column. Keeps state single-sourced.
    numberingField: text("numbering_field"),

    // #365 — which column to surface when referencing this doctype from
    // another view (list columns, FK dropdowns, search results). Fallback
    // order: displayField → numberingField → row.id. Module-declared.
    displayField: text("display_field"),

    // #365 — column that holds the record's primary image URL (avatar /
    // thumbnail / logo). Consumed by list avatars, FK dropdowns, detail
    // headers. null = no image surface; UI falls back to a generic icon.
    // Examples: logo_url, avatar_url, photo_url.
    imageField: text("image_field"),

    // #365 — columns to query when the user types in global search or an
    // FK picker. Stored as JSON array of column names. null = fall back
    // to displayField (or numberingField). Example: ["party_name",
    // "status", "invoice_no"]. The column names are validated against
    // the physical tableName at write time to catch typos early.
    searchFields: text("search_fields"),

    // #365 follow-up — Thai UI labels for each column on the doctype's
    // table. Stored as JSON object `{ colname: "ชื่อภาษาไทย" }`. Surfaces
    // in the Admin sidebar column list so admins see the user-facing
    // label (e.g. "เลขที่ใบเสนอราคา") instead of the raw column type.
    // null = no labels declared; UI falls back to showing the column name.
    // Validated at the service layer (keys must be actual columns on
    // tableName; values must be non-empty strings).
    columnLabels: text("column_labels"),

    // #496 — canonical document lifecycle contract for submittable ERP
    // documents. JSON object owned by module code (not admin-editable):
    // status/docStatus fields, immutable states, allowed actions, Thai labels,
    // and reversal policy (status-only, GL reversal, stock reversal, etc.).
    lifecycle: text("lifecycle"),

    createdAt: text("created_at").notNull(),
    updatedAt: text("updated_at").notNull(),
  },
  (table) => [
    // Enforce one platform default + one per-company override per doctype id.
    uniqueIndex("uq_doctype_registry_id_company").on(table.id, table.companyId),
    // Partial UNIQUE that closes the "two platform defaults" gap SQLite's
    // plain UNIQUE leaves open (treats NULL as distinct). At most one row
    // per doctype id can have company_id IS NULL — the platform default.
    uniqueIndex("uq_doctype_registry_default")
      .on(table.id)
      .where(sql`${table.companyId} IS NULL`),
    // Fast module-scoped lookups ("give me every doctype in sales").
    index("idx_doctype_registry_module").on(table.moduleId),
    // Fast per-company override lookup — id first to support point-reads
    // of "company X's override of doctype Y".
    index("idx_doctype_registry_company_id").on(table.companyId, table.id),
    // Defense-in-depth: reject malformed feature JSON before it hits the DB.
    // Service-layer isDoctypeFeature validates per-item; this guards the
    // structure so even a misbehaving cron can't poison the registry.
    check("features_valid_json", sql`json_valid(${table.features})`),
    // Mirrors the 0 | 1 | 2 tier system documented in rules/module-boundaries.md.
    check("tier_in_range", sql`${table.tier} IN (0, 1, 2)`),
    check(
      "kind_in_range",
      sql`${table.kind} IN ('normal', 'child_table', 'submittable', 'ledger', 'junction', 'system', 'single')`,
    ),
    // #365 — search_fields is nullable, but non-null values must be valid JSON.
    check(
      "search_fields_valid_json",
      sql`${table.searchFields} IS NULL OR json_valid(${table.searchFields})`,
    ),
    // #365 follow-up — column_labels is nullable JSON object. No DB-level
    // CHECK: adding one retroactively would require a full table rebuild
    // (SQLite can't ALTER TABLE ADD CHECK), and the service layer already
    // validates the JSON shape on every write via updateDoctypeMetadata().
    // #496 — lifecycle is new enough to add with a table rebuild migration,
    // so it gets the DB-level JSON guard as well.
    check(
      "lifecycle_valid_json",
      sql`${table.lifecycle} IS NULL OR json_valid(${table.lifecycle})`,
    ),
    check(
      "catalog_metadata_valid_json",
      sql`${table.catalogMetadata} IS NULL OR json_valid(${table.catalogMetadata})`,
    ),
  ],
);

export type DoctypeRegistry = typeof doctypeRegistry.$inferSelect;
export type NewDoctypeRegistry = typeof doctypeRegistry.$inferInsert;
